<template>
  <div class="game-management">
    <div class="page-header">
      <h2>游戏管理</h2>
      <p>管理游戏相关功能</p>
    </div>

    <div class="page-content">
      <!-- 搜索区域 -->
      <div class="search-section">
        <div class="search-box">
          <input v-model="searchKeyword" type="text" placeholder="请输入游戏名称搜索" class="search-input"
            @keyup.enter="handleSearch" />
          <select v-model="searchCategory" class="category-select">
            <option value="">全部分类</option>
            <option value="1">赛车竞速</option>
            <option value="2">冒险闯关</option>
            <option value="3">情侣互动</option>
            <option value="4">恐怖惊悚</option>
            <option value="5">体育运动</option>
            <option value="6">角色扮演</option>
            <option value="7">枪战射击</option>
            <option value="8">爽快肉鸽</option>
            <option value="9">外星太空</option>
            <option value="10">奇幻魔法</option>
            <option value="11">超级英雄</option>
            <option value="12">开放世界</option>
            <option value="13">格斗竞技</option>
            <option value="14">悬疑解密</option>
            <option value="15">策略战术</option>
            <option value="16">动漫卡通</option>
            <option value="17">即时战略</option>
            <option value="18">模拟经营</option>
          </select>

          <!-- 控制器分类多选下拉框 -->
          <div class="multi-select-container search-multi-select">
            <div class="multi-select-display" @click="toggleSearchControllerDropdown">
              <span v-if="selectedSearchControllers.length === 0" class="placeholder">控制器分类</span>
              <span v-else class="selected-items">
                {{selectedSearchControllers.map(controller => getControllerDisplayName(controller)).join(', ')}}
              </span>
              <span class="dropdown-arrow" :class="{ 'open': showSearchControllerDropdown }">▼</span>
            </div>
            <div v-if="showSearchControllerDropdown" class="multi-select-dropdown">
              <div class="dropdown-options">
                <div v-for="controller in controllerOptions" :key="controller.value" class="dropdown-option"
                  :class="{ 'selected': isSearchControllerSelected(controller.value) }"
                  @click="toggleSearchControllerSelection(controller.value)">
                  <input type="checkbox" :checked="isSearchControllerSelected(controller.value)" @click.stop />
                  <span>{{ controller.label }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 玩家人数分类多选下拉框 -->
          <div class="multi-select-container search-multi-select">
            <div class="multi-select-display" @click="toggleSearchPlayersDropdown">
              <span v-if="selectedSearchPlayers.length === 0" class="placeholder">玩家人数</span>
              <span v-else class="selected-items">
                {{selectedSearchPlayers.map(player => getPlayersDisplayName(player)).join(', ')}}
              </span>
              <span class="dropdown-arrow" :class="{ 'open': showSearchPlayersDropdown }">▼</span>
            </div>
            <div v-if="showSearchPlayersDropdown" class="multi-select-dropdown">
              <div class="dropdown-options">
                <div v-for="player in playersOptions" :key="player.value" class="dropdown-option"
                  :class="{ 'selected': isSearchPlayersSelected(player.value) }"
                  @click="toggleSearchPlayersSelection(player.value)">
                  <input type="checkbox" :checked="isSearchPlayersSelected(player.value)" @click.stop />
                  <span>{{ player.label }}</span>
                </div>
              </div>
            </div>
          </div>

          <button @click="handleSearch" class="search-btn">搜索</button>
          <button @click="handleReset" class="reset-btn">重置</button>
          <button @click="handleAdd" class="add-btn">新增游戏</button>
        </div>
      </div>

      <!-- 表格区域 -->
      <div class="table-section">
        <div class="table-header">
          <h3>游戏列表</h3>
        </div>

        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>游戏封面</th>
                <th>游戏名称</th>
                <th>游戏拼音</th>
                <th>分类</th>
                <th>状态</th>
                <th>控制器</th>
                <th>玩家人数</th>
                <th>描述</th>
                <th>发布时间</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading" class="loading-row">
                <td colspan="11" class="loading-cell">
                  <div class="loading-spinner">
                    <div class="spinner"></div>
                  </div>
                </td>
              </tr>
              <tr v-else-if="!gameList || gameList.length === 0" class="empty-row">
                <td colspan="11" class="empty-cell">暂无数据</td>
              </tr>
              <tr v-else v-for="game in gameList" :key="game.id" class="data-row">
                <td>
                  <img v-if="game.coverImage" :src="getImageUrl(game.coverImage)" :alt="game.title" class="game-cover"
                    loading="lazy" />
                  <div class="game-cover-placeholder" v-else>🎮</div>
                </td>
                <td class="game-name-cell">{{ game.title }}</td>
                <td>{{ game.pinyin || '-' }}</td>
                <td class="category-cell">
                  <div v-if="getCategoryList(game.category).length" class="category-tag-list">
                    <span
                      v-for="categoryName in getCategoryList(game.category)"
                      :key="`${game.id}-${categoryName}`"
                      class="category-tag"
                    >
                      {{ categoryName }}
                    </span>
                  </div>
                  <span v-else>-</span>
                </td>
                <td>
                  <button
                    type="button"
                    class="status-toggle-btn"
                    :disabled="isStatusUpdating(game.id)"
                    :title="`点击${getToggleStatusAction(game.status)}`"
                    @click="handleToggleStatus(game)"
                  >
                    <span :class="['status-badge', getStatusClass(game.status), { loading: isStatusUpdating(game.id) }]">
                      <span class="status-dot"></span>
                      {{ isStatusUpdating(game.id) ? '处理中...' : getStatusName(game.status) }}
                    </span>
                  </button>
                </td>
                <td>{{ getControllerName(game.controller) }}</td>
                <td>{{ getPlayersName(game.players) }}</td>
                <td class="description-cell">
                  <div v-if="game.description" class="description-wrapper">
                    <div
                      :data-game-id="game.id"
                      :class="['description-text', { expanded: isDescriptionExpanded(game.id) }]"
                    >
                      {{ game.description }}
                    </div>
                    <button
                      v-if="hasDescriptionOverflow(game.id)"
                      type="button"
                      class="description-toggle-btn"
                      @click="toggleDescriptionExpand(game.id)"
                    >
                      {{ isDescriptionExpanded(game.id) ? '收起' : '展开' }}
                    </button>
                  </div>
                  <span v-else>-</span>
                </td>
                <td>{{ formatDate(game.publishTime) }}</td>
                <td>{{ formatDate(game.createdTime) }}</td>
                <td>
                  <div class="actions-container">
                    <button @click="handleEdit(game)" class="action-btn edit-btn">编辑</button>
                    <button @click="handleDelete(game)" class="action-btn delete-btn">删除</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 分页区域 -->
        <div class="pagination-section" v-if="pagination && pagination.totalPages > 1">
          <div class="pagination-info">
            共 {{ pagination.totalElements }} 条记录，第 {{ pagination.number + 1 }} / {{ pagination.totalPages }} 页
          </div>
          <div class="pagination-controls">
            <button @click="handlePageChange(0)" :disabled="pagination.first" class="page-btn">
              首页
            </button>
            <button @click="handlePageChange(pagination.number - 1)" :disabled="pagination.first" class="page-btn">
              上一页
            </button>
            <span class="page-numbers">
              <button v-for="page in visiblePages" :key="page" @click="handlePageChange(page - 1)"
                :class="['page-number', { active: page === pagination.number + 1 }]" class="page-btn">
                {{ page }}
              </button>
            </span>
            <button @click="handlePageChange(pagination.number + 1)" :disabled="pagination.last" class="page-btn">
              下一页
            </button>
            <button @click="handlePageChange(pagination.totalPages - 1)" :disabled="pagination.last" class="page-btn">
              末页
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 新增游戏模态框 -->
    <div v-if="showAddModal" class="modal-overlay">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>新增游戏</h3>
          <button @click="handleCloseAddModal" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleAddSubmit" class="game-form">
            <div class="form-group">
              <label for="title" class="required">游戏名称</label>
              <input id="title" v-model="addFormData.title" type="text" class="form-input"
                :class="{ 'error': addFormErrors.title }" placeholder="请输入游戏名称" required />
              <span v-if="addFormErrors.title" class="error-message">{{ addFormErrors.title }}</span>
            </div>

            <div class="form-group">
              <label for="pinyin">游戏拼音</label>
              <input id="pinyin" v-model="addFormData.pinyin" type="text" class="form-input" placeholder="请输入游戏拼音" />
            </div>

            <div class="form-group">
              <label for="category" class="required">游戏分类</label>
              <div class="multi-select-container">
                <div class="multi-select-display" :class="{ 'error': addFormErrors.category }"
                  @click="toggleCategoryDropdown">
                  <span v-if="selectedCategories.length === 0" class="placeholder">请选择游戏分类</span>
                  <span v-else class="selected-items">
                    {{selectedCategories.map(category => getCategoryDisplayName(category)).join(', ')}}
                  </span>
                  <span class="dropdown-arrow" :class="{ 'open': showCategoryDropdown }">▼</span>
                </div>
                <div v-if="showCategoryDropdown" class="multi-select-dropdown">
                  <div class="dropdown-options">
                    <div v-for="category in categoryOptions" :key="category.value" class="dropdown-option"
                      :class="{ 'selected': isCategorySelected(category.value) }"
                      @click="toggleCategorySelection(category.value)">
                      <input type="checkbox" :checked="isCategorySelected(category.value)" @click.stop />
                      <span>{{ category.label }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <span v-if="addFormErrors.category" class="error-message">{{ addFormErrors.category }}</span>
            </div>

            <div class="form-group">
              <label for="controller" class="required">控制器</label>
              <div class="multi-select-container">
                <div class="multi-select-display" :class="{ 'error': addFormErrors.controller }"
                  @click="toggleControllerDropdown">
                  <span v-if="selectedControllers.length === 0" class="placeholder">请选择控制器</span>
                  <span v-else class="selected-items">
                    {{selectedControllers.map(controller => getControllerDisplayName(controller)).join(', ')}}
                  </span>
                  <span class="dropdown-arrow" :class="{ 'open': showControllerDropdown }">▼</span>
                </div>
                <div v-if="showControllerDropdown" class="multi-select-dropdown">
                  <div class="dropdown-options">
                    <div v-for="controller in controllerOptions" :key="controller.value" class="dropdown-option"
                      :class="{ 'selected': isControllerSelected(controller.value) }"
                      @click="toggleControllerSelection(controller.value)">
                      <input type="checkbox" :checked="isControllerSelected(controller.value)" @click.stop />
                      <span>{{ controller.label }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <span v-if="addFormErrors.controller" class="error-message">{{ addFormErrors.controller }}</span>
            </div>

            <div class="form-group">
              <label for="players" class="required">玩家人数</label>
              <div class="multi-select-container">
                <div class="multi-select-display" :class="{ 'error': addFormErrors.players }"
                  @click="togglePlayersDropdown">
                  <span v-if="selectedPlayers.length === 0" class="placeholder">请选择玩家人数</span>
                  <span v-else class="selected-items">
                    {{selectedPlayers.map(player => getPlayersDisplayName(player)).join(', ')}}
                  </span>
                  <span class="dropdown-arrow" :class="{ 'open': showPlayersDropdown }">▼</span>
                </div>
                <div v-if="showPlayersDropdown" class="multi-select-dropdown">
                  <div class="dropdown-options">
                    <div v-for="player in playersOptions" :key="player.value" class="dropdown-option"
                      :class="{ 'selected': isPlayersSelected(player.value) }"
                      @click="togglePlayersSelection(player.value)">
                      <input type="checkbox" :checked="isPlayersSelected(player.value)" @click.stop />
                      <span>{{ player.label }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <span v-if="addFormErrors.players" class="error-message">{{ addFormErrors.players }}</span>
            </div>

            <div class="form-group">
              <label for="description">游戏描述</label>
              <textarea id="description" v-model="addFormData.description" placeholder="请输入游戏描述（可选）"
                class="form-textarea" rows="3"></textarea>
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="addFormData.chineseVoice" class="checkbox-input" />
                <span class="checkbox-text">中文语音</span>
                <span class="checkbox-hint">（勾选后会在游戏封面右上角显示"中文语音"标签）</span>
              </label>
            </div>

            <div class="form-group">
              <label for="localPath" class="required">游戏路径</label>
              <input id="localPath" v-model="addFormData.localPath" type="text" class="form-input"
                :class="{ 'error': addFormErrors.localPath }" placeholder="本地EXE/BAT路径，或 http/steam 链接" required />
              <span class="checkbox-hint">（支持本地可执行文件、脚本文件或外部协议链接）</span>
              <span v-if="addFormErrors.localPath" class="error-message">{{ addFormErrors.localPath }}</span>
            </div>

            <div class="form-group">
              <label for="executionParameters">执行参数</label>
              <input id="executionParameters" v-model="addFormData.executionParameters" type="text" class="form-input"
                placeholder="例如：-windowed -vulkan（可选）" />
              <span class="checkbox-hint">（仅对本地可执行文件生效）</span>
            </div>

            <div class="form-group">
              <label>游戏封面</label>
              <div class="file-upload-section">
                <input ref="coverImageInput" type="file" accept="image/*" @change="handleCoverImageUpload"
                  class="file-input" />
                <button type="button" @click="$refs.coverImageInput.click()" class="upload-btn">
                  选择封面图片
                </button>
                <div v-if="addFormData.coverImage" class="file-info">
                  <span class="file-name">已选择: {{ getFileName(addFormData.coverImage) }}</span>
                  <button type="button" @click="removeCoverImage" class="remove-btn">移除</button>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label>游戏视频</label>
              <div class="file-upload-section">
                <input ref="videoInput" type="file" accept="video/*" @change="handleVideoUpload" class="file-input" />
                <button type="button" @click="$refs.videoInput.click()" class="upload-btn">
                  选择游戏视频
                </button>
                <div v-if="addFormData.videoUrl" class="file-info">
                  <span class="file-name">已选择: {{ getFileName(addFormData.videoUrl) }}</span>
                  <button type="button" @click="removeVideo" class="remove-btn">移除</button>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label>详情图片</label>
              <div class="file-upload-section">
                <input ref="detailImageInput" type="file" accept="image/*" multiple @change="handleDetailImageUpload"
                  class="file-input" />
                <button type="button" @click="$refs.detailImageInput.click()" class="upload-btn">
                  选择详情图片（可多选）
                </button>
                <div v-if="addFormData.detailImages.length > 0" class="file-list">
                  <div v-for="(image, index) in addFormData.detailImages" :key="index" class="file-item">
                    <span class="file-name">{{ getFileName(image) }}</span>
                    <button type="button" @click="removeDetailImage(index)" class="remove-btn">移除</button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button @click="handleCloseAddModal" class="cancel-btn">取消</button>
          <button @click="handleAddSubmit" :disabled="addFormLoading" class="submit-btn">
            {{ addFormLoading ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 编辑游戏模态框 -->
    <div v-if="showEditModal" class="modal-overlay">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>编辑游戏</h3>
          <button @click="handleCloseEditModal" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleEditSubmit" class="game-form">
            <div class="form-group">
              <label for="editTitle" class="required">游戏名称</label>
              <input id="editTitle" v-model="editFormData.title" type="text" class="form-input"
                :class="{ 'error': editFormErrors.title }" placeholder="请输入游戏名称" required />
              <span v-if="editFormErrors.title" class="error-message">{{ editFormErrors.title }}</span>
            </div>

            <div class="form-group">
              <label for="editPinyin">游戏拼音</label>
              <input id="editPinyin" v-model="editFormData.pinyin" type="text" class="form-input"
                placeholder="请输入游戏拼音" />
            </div>

            <div class="form-group">
              <label for="editCategory" class="required">游戏分类</label>
              <div class="multi-select-container">
                <div class="multi-select-display" :class="{ 'error': editFormErrors.category }"
                  @click="toggleEditCategoryDropdown">
                  <span v-if="selectedEditCategories.length === 0" class="placeholder">请选择游戏分类</span>
                  <span v-else class="selected-items">
                    {{selectedEditCategories.map(category => getCategoryDisplayName(category)).join(', ')}}
                  </span>
                  <span class="dropdown-arrow" :class="{ 'open': showEditCategoryDropdown }">▼</span>
                </div>
                <div v-if="showEditCategoryDropdown" class="multi-select-dropdown">
                  <div class="dropdown-options">
                    <div v-for="category in categoryOptions" :key="category.value" class="dropdown-option"
                      :class="{ 'selected': isEditCategorySelected(category.value) }"
                      @click="toggleEditCategorySelection(category.value)">
                      <input type="checkbox" :checked="isEditCategorySelected(category.value)" @click.stop />
                      <span>{{ category.label }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <span v-if="editFormErrors.category" class="error-message">{{ editFormErrors.category }}</span>
            </div>

            <div class="form-group">
              <label for="editController" class="required">控制器</label>
              <div class="multi-select-container">
                <div class="multi-select-display" :class="{ 'error': editFormErrors.controller }"
                  @click="toggleEditControllerDropdown">
                  <span v-if="selectedEditControllers.length === 0" class="placeholder">请选择控制器</span>
                  <span v-else class="selected-items">
                    {{selectedEditControllers.map(controller => getControllerDisplayName(controller)).join(', ')}}
                  </span>
                  <span class="dropdown-arrow" :class="{ 'open': showEditControllerDropdown }">▼</span>
                </div>
                <div v-if="showEditControllerDropdown" class="multi-select-dropdown">
                  <div class="dropdown-options">
                    <div v-for="controller in controllerOptions" :key="controller.value" class="dropdown-option"
                      :class="{ 'selected': isEditControllerSelected(controller.value) }"
                      @click="toggleEditControllerSelection(controller.value)">
                      <input type="checkbox" :checked="isEditControllerSelected(controller.value)" @click.stop />
                      <span>{{ controller.label }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <span v-if="editFormErrors.controller" class="error-message">{{ editFormErrors.controller }}</span>
            </div>

            <div class="form-group">
              <label for="editPlayers" class="required">玩家人数</label>
              <div class="multi-select-container">
                <div class="multi-select-display" :class="{ 'error': editFormErrors.players }"
                  @click="toggleEditPlayersDropdown">
                  <span v-if="selectedEditPlayers.length === 0" class="placeholder">请选择玩家人数</span>
                  <span v-else class="selected-items">
                    {{selectedEditPlayers.map(player => getPlayersDisplayName(player)).join(', ')}}
                  </span>
                  <span class="dropdown-arrow" :class="{ 'open': showEditPlayersDropdown }">▼</span>
                </div>
                <div v-if="showEditPlayersDropdown" class="multi-select-dropdown">
                  <div class="dropdown-options">
                    <div v-for="player in playersOptions" :key="player.value" class="dropdown-option"
                      :class="{ 'selected': isEditPlayersSelected(player.value) }"
                      @click="toggleEditPlayersSelection(player.value)">
                      <input type="checkbox" :checked="isEditPlayersSelected(player.value)" @click.stop />
                      <span>{{ player.label }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <span v-if="editFormErrors.players" class="error-message">{{ editFormErrors.players }}</span>
            </div>

            <div class="form-group">
              <label for="editDescription">游戏描述</label>
              <textarea id="editDescription" v-model="editFormData.description" placeholder="请输入游戏描述（可选）"
                class="form-textarea" rows="3"></textarea>
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="editFormData.chineseVoice" class="checkbox-input" />
                <span class="checkbox-text">中文语音</span>
                <span class="checkbox-hint">（勾选后会在游戏封面右上角显示"中文语音"标签）</span>
              </label>
            </div>

            <div class="form-group">
              <label for="editLocalPath" class="required">游戏路径</label>
              <input id="editLocalPath" v-model="editFormData.localPath" type="text" class="form-input"
                :class="{ 'error': editFormErrors.localPath }" placeholder="本地EXE/BAT路径，或 http/steam 链接" required />
              <span class="checkbox-hint">（支持本地可执行文件、脚本文件或外部协议链接）</span>
              <span v-if="editFormErrors.localPath" class="error-message">{{ editFormErrors.localPath }}</span>
            </div>

            <div class="form-group">
              <label for="editExecutionParameters">执行参数</label>
              <input id="editExecutionParameters" v-model="editFormData.executionParameters" type="text"
                class="form-input" placeholder="例如：-windowed -vulkan（可选）" />
              <span class="checkbox-hint">（仅对本地可执行文件生效）</span>
            </div>

            <div class="form-group">
              <label>游戏封面</label>
              <div class="file-upload-section">
                <input ref="editCoverImageInput" type="file" accept="image/*" @change="handleEditCoverImageUpload"
                  class="file-input" />
                <button type="button" @click="$refs.editCoverImageInput.click()" class="upload-btn">
                  选择封面图片
                </button>
                <div v-if="editFormData.coverImage" class="file-info file-info-with-preview">
                  <img :src="getImageUrl(editFormData.coverImage)" class="file-preview-image" alt="封面预览"
                    loading="lazy" />
                  <span class="file-name">{{ getFileName(editFormData.coverImage) }}</span>
                  <button type="button" @click="removeEditCoverImage" class="remove-btn">移除</button>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label>游戏视频</label>
              <div class="file-upload-section">
                <input ref="editVideoInput" type="file" accept="video/*" @change="handleEditVideoUpload"
                  class="file-input" />
                <button type="button" @click="$refs.editVideoInput.click()" class="upload-btn">
                  选择游戏视频
                </button>
                <div v-if="editFormData.videoUrl" class="file-info">
                  <span class="file-name">已上传: {{ getFileName(editFormData.videoUrl) }}</span>
                  <button type="button" @click="removeEditVideo" class="remove-btn">移除</button>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label>详情图片</label>
              <div class="file-upload-section">
                <input ref="editDetailImageInput" type="file" accept="image/*" multiple
                  @change="handleEditDetailImageUpload" class="file-input" />
                <button type="button" @click="$refs.editDetailImageInput.click()" class="upload-btn">
                  选择详情图片（可多选）
                </button>
                <div v-if="editFormData.detailImages.length > 0" class="file-list">
                  <div v-for="(image, index) in editFormData.detailImages" :key="index"
                    class="file-item file-item-with-preview">
                    <img :src="getImageUrl(image)" class="file-preview-image-small" alt="详情图预览" loading="lazy" />
                    <span class="file-name">{{ getFileName(image) }}</span>
                    <button type="button" @click="removeEditDetailImage(index)" class="remove-btn">移除</button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button @click="handleCloseEditModal" class="cancel-btn">取消</button>
          <button @click="handleEditSubmit" :disabled="editFormLoading" class="submit-btn">
            {{ editFormLoading ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'GameManagement',
  data() {
    return {
      gameList: [],
      loading: false,
      searchKeyword: '',
      searchCategory: '',
      selectedSearchControllers: [],
      showSearchControllerDropdown: false,
      selectedSearchPlayers: [],
      showSearchPlayersDropdown: false,
      pagination: {
        number: 0,
        size: 20,
        totalElements: 0,
        totalPages: 0,
        first: true,
        last: true
      },
      showAddModal: false,
      addFormLoading: false,
      addFormData: {
        title: '',
        pinyin: '',
        coverImage: '',
        videoUrl: '',
        localPath: '',
        category: '',
        controller: '',
        players: '',
        description: '',
        detailImages: [],
        chineseVoice: false
      },
      addFormErrors: {},
      selectedControllers: [],
      showControllerDropdown: false,
      controllerOptions: [
        { value: '1', label: '方向盘' },
        { value: '2', label: '手柄' },
        { value: '3', label: '鼠标' }
      ],
      selectedPlayers: [],
      showPlayersDropdown: false,
      statusUpdatingIds: [],
      expandedDescriptionIds: [],
      overflowDescriptionIds: [],
      playersOptions: [
        { value: '1', label: '单人' },
        { value: '2', label: '双人' },
        { value: '3', label: '本地多人' },
        { value: '4', label: '多人联机' }
      ],
      selectedCategories: [],
      showCategoryDropdown: false,
      categoryOptions: [
        { value: '1', label: '赛车竞速' },
        { value: '2', label: '冒险闯关' },
        { value: '3', label: '情侣互动' },
        { value: '4', label: '恐怖惊悚' },
        { value: '5', label: '体育运动' },
        { value: '6', label: '角色扮演' },
        { value: '7', label: '枪战射击' },
        { value: '8', label: '爽快肉鸽' },
        { value: '9', label: '外星太空' },
        { value: '10', label: '奇幻魔法' },
        { value: '11', label: '超级英雄' },
        { value: '12', label: '开放世界' },
        { value: '13', label: '格斗竞技' },
        { value: '14', label: '悬疑解密' },
        { value: '15', label: '策略战术' },
        { value: '16', label: '动漫卡通' },
        { value: '17', label: '即时战略' },
        { value: '18', label: '模拟经营' }
      ],
      showEditModal: false,
      editFormLoading: false,
      editFormData: {
        id: null,
        title: '',
        pinyin: '',
        coverImage: '',
        videoUrl: '',
        localPath: '',
        category: '',
        controller: '',
        players: '',
        description: '',
        detailImages: [],
        chineseVoice: false
      },
      editFormErrors: {},
      selectedEditControllers: [],
      showEditControllerDropdown: false,
      selectedEditPlayers: [],
      showEditPlayersDropdown: false,
      selectedEditCategories: [],
      showEditCategoryDropdown: false
    }
  },
  computed: {
    visiblePages() {
      if (!this.pagination || !this.pagination.totalPages) {
        return []
      }

      const current = this.pagination.number + 1
      const total = this.pagination.totalPages
      const pages = []

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i)
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(total)
        } else if (current >= total - 3) {
          pages.push(1)
          pages.push('...')
          for (let i = total - 4; i <= total; i++) {
            pages.push(i)
          }
        } else {
          pages.push(1)
          pages.push('...')
          for (let i = current - 1; i <= current + 1; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(total)
        }
      }

      return pages
    }
  },
  mounted() {
    this.loadGameList()
    document.addEventListener('click', this.handleDocumentClick)
    window.addEventListener('resize', this.handleWindowResize)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick)
    window.removeEventListener('resize', this.handleWindowResize)
  },
  methods: {
    async loadGameList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.number,
          size: this.pagination.size,
          category: this.searchCategory || undefined,
          keyword: this.searchKeyword || undefined
        }

        // 添加控制器参数
        if (this.selectedSearchControllers.length > 0) {
          params.controllers = this.selectedSearchControllers.join(',')
        }

        // 添加玩家人数参数
        if (this.selectedSearchPlayers.length > 0) {
          params.players = this.selectedSearchPlayers.join(',')
        }

        const response = await request.get('/api/games/getGames', {
          params: params
        })

        if (response.data.success && response.data.code === 200) {
          this.gameList = response.data.data.content || []
          this.pagination = {
            number: response.data.data.number || 0,
            size: response.data.data.size || 20,
            totalElements: response.data.data.totalElements || 0,
            totalPages: response.data.data.totalPages || 0,
            first: response.data.data.first || true,
            last: response.data.data.last || true
          }
        } else {
          throw new Error(response.data.message || '获取游戏列表失败')
        }
      } catch (error) {
        console.error('获取游戏列表失败:', error)
        this.gameList = []
        this.expandedDescriptionIds = []
        this.overflowDescriptionIds = []
        this.pagination = {
          number: 0,
          size: 20,
          totalElements: 0,
          totalPages: 0,
          first: true,
          last: true
        }
      } finally {
        this.loading = false
        this.$nextTick(() => {
          this.updateDescriptionOverflow()
        })
      }
    },

    handleSearch() {
      this.pagination.number = 0
      this.loadGameList()
    },

    handleReset() {
      this.searchKeyword = ''
      this.searchCategory = ''
      this.selectedSearchControllers = []
      this.showSearchControllerDropdown = false
      this.selectedSearchPlayers = []
      this.showSearchPlayersDropdown = false
      this.pagination.number = 0
      this.loadGameList()
    },

    handlePageChange(pageNumber) {
      if (pageNumber >= 0 && pageNumber < this.pagination.totalPages) {
        this.pagination.number = pageNumber
        this.loadGameList()
      }
    },

    getCategoryName(category) {
      if (!category || !category.trim()) {
        return '-'
      }

      const categoryMap = {
        '1': '赛车竞速',
        '2': '冒险闯关',
        '3': '情侣互动',
        '4': '恐怖惊悚',
        '5': '体育运动',
        '6': '角色扮演',
        '7': '枪战射击',
        '8': '爽快肉鸽',
        '9': '外星太空',
        '10': '奇幻魔法',
        '11': '超级英雄',
        '12': '开放世界',
        '13': '格斗竞技',
        '14': '悬疑解密',
        '15': '策略战术',
        '16': '动漫卡通',
        '17': '即时战略',
        '18': '模拟经营'
      }

      // 按逗号分隔字符串
      const categoryIds = category.split(',').map(id => id.trim())

      // 映射每个ID为中文名称
      const categoryNames = categoryIds.map(id => categoryMap[id] || '未知')

      // 用逗号连接所有中文名称
      return categoryNames.join(', ')
    },

    getCategoryList(category) {
      const categoryName = this.getCategoryName(category)
      if (!categoryName || categoryName === '-') {
        return []
      }
      return categoryName.split(',').map(item => item.trim()).filter(Boolean)
    },

    getControllerName(controller) {
      if (!controller || !controller.trim()) {
        return '-'
      }

      const controllerMap = {
        '1': '方向盘',
        '2': '手柄',
        '3': '鼠标'
      }

      // 按逗号分隔字符串
      const controllerIds = controller.split(',').map(id => id.trim())

      // 映射每个ID为中文名称
      const controllerNames = controllerIds.map(id => controllerMap[id] || '未知')

      // 用逗号连接所有中文名称
      return controllerNames.join(', ')
    },

    getPlayersName(players) {
      if (!players || !players.trim()) {
        return '-'
      }

      const playersMap = {
        '1': '单人',
        '2': '双人',
        '3': '本地多人',
        '4': '多人联机'
      }

      // 按逗号分隔字符串
      const playerIds = players.split(',').map(id => id.trim())

      // 映射每个ID为中文名称
      const playerNames = playerIds.map(id => playersMap[id] || '未知')

      // 用逗号连接所有中文名称
      return playerNames.join(', ')
    },

    getStatusName(status) {
      const statusMap = {
        'ACTIVE': '启用',
        'INACTIVE': '禁用',
        'DRAFT': '草稿'
      }
      return statusMap[status] || status
    },

    getStatusClass(status) {
      const statusClassMap = {
        'ACTIVE': 'active',
        'INACTIVE': 'inactive',
        'DRAFT': 'draft'
      }
      return statusClassMap[status] || 'inactive'
    },

    getToggleStatusAction(status) {
      return status === 'ACTIVE' ? '停用' : '启用'
    },

    isStatusUpdating(gameId) {
      return this.statusUpdatingIds.includes(String(gameId))
    },

    getImageUrl(imagePath) {
      if (!imagePath) return ''
      // 如果已经是完整URL，直接返回
      if (imagePath.startsWith('http')) {
        return imagePath
      }
      // 获取当前域名，自适应环境
      const baseUrl = window.location.origin
      // 处理路径：去掉 /home/games/ 或 /home/games// 前缀，映射到 /api/files/
      let relativePath = imagePath
        .replace(/^\/home\/games\/+/, '')  // 去掉 /home/games/ 或 /home/games// 前缀
        .replace(/^D:\/games\/+/i, '')     // 兼容旧的 Windows 路径
        .replace(/^D:\\games\\+/i, '')     // 兼容旧的 Windows 反斜杠路径
      return `${baseUrl}/api/files/${relativePath}`
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },

    handleWindowResize() {
      this.updateDescriptionOverflow()
    },

    updateDescriptionOverflow() {
      const overflowIds = []
      const elements = this.$el ? this.$el.querySelectorAll('.description-text') : []

      elements.forEach((element) => {
        const gameId = String(element.dataset.gameId)
        const wasExpanded = element.classList.contains('expanded')
        if (wasExpanded) {
          element.classList.remove('expanded')
        }

        const isOverflowing = element.scrollHeight > element.clientHeight + 1

        if (wasExpanded) {
          element.classList.add('expanded')
        }

        if (isOverflowing) {
          overflowIds.push(gameId)
        }
      })

      this.overflowDescriptionIds = overflowIds
      this.expandedDescriptionIds = this.expandedDescriptionIds.filter(id => overflowIds.includes(String(id)))
    },

    hasDescriptionOverflow(gameId) {
      return this.overflowDescriptionIds.includes(String(gameId))
    },

    isDescriptionExpanded(gameId) {
      return this.expandedDescriptionIds.includes(String(gameId))
    },

    toggleDescriptionExpand(gameId) {
      const id = String(gameId)
      const index = this.expandedDescriptionIds.indexOf(id)
      if (index > -1) {
        this.expandedDescriptionIds.splice(index, 1)
      } else {
        this.expandedDescriptionIds.push(id)
      }

      this.$nextTick(() => {
        this.updateDescriptionOverflow()
      })
    },

    handleEdit(game) {
      this.showEditModal = true
      this.initEditFormData(game)
    },

    async handleToggleStatus(game) {
      const nextStatus = game.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
      const actionText = nextStatus === 'ACTIVE' ? '启用' : '停用'
      const gameId = String(game.id)

      if (!confirm(`确定要${actionText}游戏 "${game.title}" 吗？`)) {
        return
      }

      this.statusUpdatingIds.push(gameId)
      try {
        const response = await request.put(`/api/games/updateGameStatus/${game.id}`, null, {
          params: {
            status: nextStatus
          }
        })

        if (response.data.success && response.data.code === 200) {
          this.loadGameList()
        } else {
          throw new Error(response.data.message || `${actionText}游戏失败`)
        }
      } catch (error) {
        console.error(`${actionText}游戏失败:`, error)
        alert(error.message || `${actionText}游戏失败，请重试`)
      } finally {
        this.statusUpdatingIds = this.statusUpdatingIds.filter(id => id !== gameId)
      }
    },

    async handleDelete(game) {
      if (confirm(`确定要删除游戏 "${game.title}" 吗？`)) {
        try {
          const response = await request.delete(`/api/games/deleteGame?id=${game.id}`)

          if (response.data.success && response.data.code === 200) {
            this.loadGameList()
          } else {
            throw new Error(response.data.message || '删除游戏失败')
          }
        } catch (error) {
          console.error('删除游戏失败:', error)
          alert(error.message || '删除游戏失败，请重试')
        }
      }
    },

    // 新增游戏相关方法
    handleAdd() {
      this.showAddModal = true
      this.resetAddForm()
    },

    handleCloseAddModal() {
      this.showAddModal = false
      this.resetAddForm()
    },

    resetAddForm() {
      this.addFormData = {
        title: '',
        pinyin: '',
        coverImage: '',
        videoUrl: '',
        localPath: '',
        category: '',
        controller: '',
        players: '',
        description: '',
        detailImages: []
      }
      this.addFormErrors = {}
      this.addFormLoading = false
      this.selectedControllers = []
      this.showControllerDropdown = false
      this.selectedPlayers = []
      this.showPlayersDropdown = false
      this.selectedCategories = []
      this.showCategoryDropdown = false
    },

    validateAddForm() {
      this.addFormErrors = {}
      let isValid = true

      if (!this.addFormData.title.trim()) {
        this.addFormErrors.title = '请输入游戏名称'
        isValid = false
      }

      if (this.selectedCategories.length === 0) {
        this.addFormErrors.category = '请选择游戏分类'
        isValid = false
      }

      if (this.selectedControllers.length === 0) {
        this.addFormErrors.controller = '请选择控制器'
        isValid = false
      }

      if (this.selectedPlayers.length === 0) {
        this.addFormErrors.players = '请选择玩家人数'
        isValid = false
      }

      if (!this.addFormData.localPath.trim()) {
        this.addFormErrors.localPath = '请输入本地路径'
        isValid = false
      }

      return isValid
    },

    async handleAddSubmit() {
      if (!this.validateAddForm()) {
        return
      }

      this.addFormLoading = true
      try {
        const response = await request.post('/api/games/createGame', {
          title: this.addFormData.title.trim(),
          pinyin: this.addFormData.pinyin.trim() || null,
          coverImage: this.addFormData.coverImage,
          videoUrl: this.addFormData.videoUrl,
          localPath: this.addFormData.localPath,
          category: this.selectedCategories.join(','),
          controller: this.selectedControllers.join(','),
          players: this.selectedPlayers.join(','),
          description: this.addFormData.description.trim(),
          detailImages: this.addFormData.detailImages,
          chineseVoice: this.addFormData.chineseVoice,
          executionParameters: this.addFormData.executionParameters
        })

        if (response.data.success && response.data.code === 200) {
          this.handleCloseAddModal()
          this.loadGameList()
        } else {
          throw new Error(response.data.message || '新增游戏失败')
        }
      } catch (error) {
        console.error('新增游戏失败:', error)
        alert(error.message || '新增游戏失败，请重试')
      } finally {
        this.addFormLoading = false
      }
    },

    // 文件上传相关方法
    async handleCoverImageUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      try {
        const formData = new FormData()
        formData.append('file', file)

        const response = await request.post('/api/games/uploadCover', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        if (response.data.success && response.data.code === 200) {
          this.addFormData.coverImage = response.data.data
        } else {
          throw new Error(response.data.message || '上传封面图片失败')
        }
      } catch (error) {
        console.error('上传封面图片失败:', error)
        alert('上传封面图片失败，请重试')
      }
    },

    async handleVideoUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      try {
        const maxVideoSize = 512 * 1024 * 1024
        if (file.size > maxVideoSize) {
          throw new Error('视频文件不能超过 512MB')
        }

        const formData = new FormData()
        formData.append('file', file)

        const response = await request.post('/api/games/uploadVideo', formData, {
          timeout: 10 * 60 * 1000,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        if (response.data.success && response.data.code === 200) {
          this.addFormData.videoUrl = response.data.data
        } else {
          throw new Error(response.data.message || '上传游戏视频失败')
        }
      } catch (error) {
        console.error('上传游戏视频失败:', error)
        const message = error.response?.data?.message || error.message || '上传游戏视频失败，请重试'
        alert(message)
      }
    },

    async handleDetailImageUpload(event) {
      const files = Array.from(event.target.files)
      if (files.length === 0) return

      try {
        for (const file of files) {
          const formData = new FormData()
          formData.append('file', file)

          const response = await request.post('/api/games/uploadDetailImage', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })

          if (response.data.success && response.data.code === 200) {
            this.addFormData.detailImages.push(response.data.data)
          } else {
            throw new Error(response.data.message || '上传详情图片失败')
          }
        }
      } catch (error) {
        console.error('上传详情图片失败:', error)
        alert('上传详情图片失败，请重试')
      }
    },

    removeCoverImage() {
      this.addFormData.coverImage = ''
      this.$refs.coverImageInput.value = ''
    },

    removeVideo() {
      this.addFormData.videoUrl = ''
      this.$refs.videoInput.value = ''
    },

    removeDetailImage(index) {
      this.addFormData.detailImages.splice(index, 1)
    },

    getFileName(filePath) {
      if (!filePath) return ''
      return filePath.split('/').pop() || filePath.split('\\').pop() || filePath
    },

    // 控制器相关方法
    toggleControllerDropdown() {
      this.showControllerDropdown = !this.showControllerDropdown
    },

    toggleControllerSelection(controllerValue) {
      const index = this.selectedControllers.indexOf(controllerValue)
      if (index > -1) {
        this.selectedControllers.splice(index, 1)
      } else {
        this.selectedControllers.push(controllerValue)
      }
    },

    isControllerSelected(controllerValue) {
      return this.selectedControllers.includes(controllerValue)
    },

    getControllerDisplayName(controllerValue) {
      const controller = this.controllerOptions.find(opt => opt.value === controllerValue)
      return controller ? controller.label : controllerValue
    },

    handleDocumentClick(event) {
      if (this.showControllerDropdown && !event.target.closest('.multi-select-container')) {
        this.showControllerDropdown = false
      }
      if (this.showPlayersDropdown && !event.target.closest('.multi-select-container')) {
        this.showPlayersDropdown = false
      }
      if (this.showEditControllerDropdown && !event.target.closest('.multi-select-container')) {
        this.showEditControllerDropdown = false
      }
      if (this.showEditPlayersDropdown && !event.target.closest('.multi-select-container')) {
        this.showEditPlayersDropdown = false
      }
      if (this.showSearchControllerDropdown && !event.target.closest('.multi-select-container')) {
        this.showSearchControllerDropdown = false
      }
      if (this.showSearchPlayersDropdown && !event.target.closest('.multi-select-container')) {
        this.showSearchPlayersDropdown = false
      }
      if (this.showCategoryDropdown && !event.target.closest('.multi-select-container')) {
        this.showCategoryDropdown = false
      }
      if (this.showEditCategoryDropdown && !event.target.closest('.multi-select-container')) {
        this.showEditCategoryDropdown = false
      }
    },

    // 搜索控制器相关方法
    toggleSearchControllerDropdown() {
      this.showSearchControllerDropdown = !this.showSearchControllerDropdown
    },

    toggleSearchControllerSelection(controllerValue) {
      const index = this.selectedSearchControllers.indexOf(controllerValue)
      if (index > -1) {
        this.selectedSearchControllers.splice(index, 1)
      } else {
        this.selectedSearchControllers.push(controllerValue)
      }
    },

    isSearchControllerSelected(controllerValue) {
      return this.selectedSearchControllers.includes(controllerValue)
    },

    // 搜索玩家人数相关方法
    toggleSearchPlayersDropdown() {
      this.showSearchPlayersDropdown = !this.showSearchPlayersDropdown
    },

    toggleSearchPlayersSelection(playerValue) {
      const index = this.selectedSearchPlayers.indexOf(playerValue)
      if (index > -1) {
        this.selectedSearchPlayers.splice(index, 1)
      } else {
        this.selectedSearchPlayers.push(playerValue)
      }
    },

    isSearchPlayersSelected(playerValue) {
      return this.selectedSearchPlayers.includes(playerValue)
    },

    // 玩家人数相关方法
    togglePlayersDropdown() {
      this.showPlayersDropdown = !this.showPlayersDropdown
    },

    togglePlayersSelection(playerValue) {
      const index = this.selectedPlayers.indexOf(playerValue)
      if (index > -1) {
        this.selectedPlayers.splice(index, 1)
      } else {
        this.selectedPlayers.push(playerValue)
      }
    },

    isPlayersSelected(playerValue) {
      return this.selectedPlayers.includes(playerValue)
    },

    getPlayersDisplayName(playerValue) {
      const player = this.playersOptions.find(opt => opt.value === playerValue)
      return player ? player.label : playerValue
    },

    // 游戏分类相关方法
    toggleCategoryDropdown() {
      this.showCategoryDropdown = !this.showCategoryDropdown
    },

    toggleCategorySelection(categoryValue) {
      const index = this.selectedCategories.indexOf(categoryValue)
      if (index > -1) {
        this.selectedCategories.splice(index, 1)
      } else {
        this.selectedCategories.push(categoryValue)
      }
    },

    isCategorySelected(categoryValue) {
      return this.selectedCategories.includes(categoryValue)
    },

    getCategoryDisplayName(categoryValue) {
      const category = this.categoryOptions.find(opt => opt.value === categoryValue)
      return category ? category.label : categoryValue
    },

    // 编辑表单游戏分类相关方法
    toggleEditCategoryDropdown() {
      this.showEditCategoryDropdown = !this.showEditCategoryDropdown
    },

    toggleEditCategorySelection(categoryValue) {
      const index = this.selectedEditCategories.indexOf(categoryValue)
      if (index > -1) {
        this.selectedEditCategories.splice(index, 1)
      } else {
        this.selectedEditCategories.push(categoryValue)
      }
    },

    isEditCategorySelected(categoryValue) {
      return this.selectedEditCategories.includes(categoryValue)
    },

    // 编辑表单控制器相关方法
    toggleEditControllerDropdown() {
      this.showEditControllerDropdown = !this.showEditControllerDropdown
    },

    toggleEditControllerSelection(controllerValue) {
      const index = this.selectedEditControllers.indexOf(controllerValue)
      if (index > -1) {
        this.selectedEditControllers.splice(index, 1)
      } else {
        this.selectedEditControllers.push(controllerValue)
      }
    },

    isEditControllerSelected(controllerValue) {
      return this.selectedEditControllers.includes(controllerValue)
    },

    // 编辑表单玩家人数相关方法
    toggleEditPlayersDropdown() {
      this.showEditPlayersDropdown = !this.showEditPlayersDropdown
    },

    toggleEditPlayersSelection(playerValue) {
      const index = this.selectedEditPlayers.indexOf(playerValue)
      if (index > -1) {
        this.selectedEditPlayers.splice(index, 1)
      } else {
        this.selectedEditPlayers.push(playerValue)
      }
    },

    isEditPlayersSelected(playerValue) {
      return this.selectedEditPlayers.includes(playerValue)
    },

    // 编辑游戏相关方法
    initEditFormData(game) {
      // 解析详情图片（后端存储为JSON字符串）
      let detailImagesArray = []
      if (game.detailImages) {
        try {
          detailImagesArray = JSON.parse(game.detailImages)
          if (!Array.isArray(detailImagesArray)) {
            detailImagesArray = []
          }
        } catch (e) {
          // 如果不是JSON格式，可能是单个路径或逗号分隔的路径
          detailImagesArray = game.detailImages.split(',').filter(s => s.trim())
        }
      }

      this.editFormData = {
        id: game.id,
        title: game.title || '',
        pinyin: game.pinyin || '',
        coverImage: game.coverImage || '',
        videoUrl: game.videoUrl || '',
        localPath: game.localPath || '',
        category: game.category || '',
        controller: game.controller || '',
        players: game.players || '',
        description: game.description || '',
        detailImages: detailImagesArray,
        chineseVoice: game.chineseVoice || false,
        executionParameters: game.executionParameters || ''
      }
      // 初始化游戏分类选择
      if (game.category) {
        this.selectedEditCategories = game.category.split(',').map(c => c.trim()).filter(c => c)
      } else {
        this.selectedEditCategories = []
      }
      // 初始化控制器选择
      if (game.controller) {
        this.selectedEditControllers = game.controller.split(',').map(c => c.trim()).filter(c => c)
      } else {
        this.selectedEditControllers = []
      }
      // 初始化玩家人数选择
      if (game.players) {
        this.selectedEditPlayers = game.players.split(',').map(p => p.trim()).filter(p => p)
      } else {
        this.selectedEditPlayers = []
      }
      this.editFormErrors = {}
      this.editFormLoading = false
      this.showEditCategoryDropdown = false
      this.showEditControllerDropdown = false
      this.showEditPlayersDropdown = false
    },

    handleCloseEditModal() {
      this.showEditModal = false
      this.resetEditForm()
    },

    resetEditForm() {
      this.editFormData = {
        id: null,
        title: '',
        pinyin: '',
        coverImage: '',
        videoUrl: '',
        localPath: '',
        category: '',
        controller: '',
        players: '',
        description: '',
        detailImages: []
      }
      this.editFormErrors = {}
      this.editFormLoading = false
      this.selectedEditCategories = []
      this.showEditCategoryDropdown = false
      this.selectedEditControllers = []
      this.showEditControllerDropdown = false
      this.selectedEditPlayers = []
      this.showEditPlayersDropdown = false
    },

    validateEditForm() {
      this.editFormErrors = {}
      let isValid = true

      if (!this.editFormData.title.trim()) {
        this.editFormErrors.title = '请输入游戏名称'
        isValid = false
      }

      if (this.selectedEditCategories.length === 0) {
        this.editFormErrors.category = '请选择游戏分类'
        isValid = false
      }

      if (this.selectedEditControllers.length === 0) {
        this.editFormErrors.controller = '请选择控制器'
        isValid = false
      }

      if (this.selectedEditPlayers.length === 0) {
        this.editFormErrors.players = '请选择玩家人数'
        isValid = false
      }

      if (!this.editFormData.localPath.trim()) {
        this.editFormErrors.localPath = '请输入本地路径'
        isValid = false
      }

      return isValid
    },

    async handleEditSubmit() {
      if (!this.validateEditForm()) {
        return
      }

      this.editFormLoading = true
      try {
        const response = await request.post(`/api/games/updateGame?id=${this.editFormData.id}`, {
          title: this.editFormData.title.trim(),
          pinyin: this.editFormData.pinyin.trim() || null,
          coverImage: this.editFormData.coverImage,
          videoUrl: this.editFormData.videoUrl,
          localPath: this.editFormData.localPath,
          category: this.selectedEditCategories.join(','),
          controller: this.selectedEditControllers.join(','),
          players: this.selectedEditPlayers.join(','),
          description: this.editFormData.description.trim(),
          detailImages: this.editFormData.detailImages,
          chineseVoice: this.editFormData.chineseVoice,
          executionParameters: this.editFormData.executionParameters
        })

        if (response.data.success && response.data.code === 200) {
          this.handleCloseEditModal()
          this.loadGameList()
        } else {
          throw new Error(response.data.message || '更新游戏失败')
        }
      } catch (error) {
        console.error('更新游戏失败:', error)
        alert(error.message || '更新游戏失败，请重试')
      } finally {
        this.editFormLoading = false
      }
    },

    // 编辑文件上传相关方法
    async handleEditCoverImageUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      try {
        const formData = new FormData()
        formData.append('file', file)

        const response = await request.post('/api/games/uploadCover', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        if (response.data.success && response.data.code === 200) {
          this.editFormData.coverImage = response.data.data
        } else {
          throw new Error(response.data.message || '上传封面图片失败')
        }
      } catch (error) {
        console.error('上传封面图片失败:', error)
        alert('上传封面图片失败，请重试')
      }
    },

    async handleEditVideoUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      try {
        const maxVideoSize = 512 * 1024 * 1024
        if (file.size > maxVideoSize) {
          throw new Error('视频文件不能超过 512MB')
        }

        const formData = new FormData()
        formData.append('file', file)

        const response = await request.post('/api/games/uploadVideo', formData, {
          timeout: 10 * 60 * 1000,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        if (response.data.success && response.data.code === 200) {
          this.editFormData.videoUrl = response.data.data
        } else {
          throw new Error(response.data.message || '上传游戏视频失败')
        }
      } catch (error) {
        console.error('上传游戏视频失败:', error)
        const message = error.response?.data?.message || error.message || '上传游戏视频失败，请重试'
        alert(message)
      }
    },

    async handleEditDetailImageUpload(event) {
      const files = Array.from(event.target.files)
      if (files.length === 0) return

      try {
        for (const file of files) {
          const formData = new FormData()
          formData.append('file', file)

          const response = await request.post('/api/games/uploadDetailImage', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })

          if (response.data.success && response.data.code === 200) {
            this.editFormData.detailImages.push(response.data.data)
          } else {
            throw new Error(response.data.message || '上传详情图片失败')
          }
        }
      } catch (error) {
        console.error('上传详情图片失败:', error)
        alert('上传详情图片失败，请重试')
      }
    },

    removeEditCoverImage() {
      this.editFormData.coverImage = ''
      this.$refs.editCoverImageInput.value = ''
    },

    removeEditVideo() {
      this.editFormData.videoUrl = ''
      this.$refs.editVideoInput.value = ''
    },

    removeEditDetailImage(index) {
      this.editFormData.detailImages.splice(index, 1)
    }
  }
}
</script>

<style scoped>
.game-management {
  height: 100%;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  font-size: 24px;
  color: #333;
  margin: 0 0 8px 0;
}

.page-header p {
  color: #666;
  margin: 0;
  font-size: 14px;
}

.page-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 搜索区域样式 */
.search-section {
  margin-bottom: 24px;
}

.search-box {
  display: flex;
  gap: 12px;
  align-items: center;
}

.search-input {
  flex: 1;
  max-width: 300px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.search-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.category-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
  min-width: 120px;
}

.category-select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.search-btn,
.reset-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.search-btn {
  background-color: #007bff;
  color: white;
}

.search-btn:hover {
  background-color: #0056b3;
}

.reset-btn {
  background-color: #6c757d;
  color: white;
}

.reset-btn:hover {
  background-color: #545b62;
}

.add-btn {
  padding: 8px 16px;
  background: #52c41a;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s ease;
}

.add-btn:hover {
  background: #73d13d;
}

/* 表格区域样式 */
.table-section {
  margin-bottom: 24px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.table-header h3 {
  font-size: 18px;
  color: #333;
  margin: 0;
}

.table-container {
  overflow-x: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table th {
  background-color: #f8f9fa;
  color: #333;
  font-weight: 600;
  padding: 12px 8px;
  text-align: left;
  border-bottom: 1px solid #ddd;
  white-space: nowrap;
}

.data-table td {
  padding: 16px 12px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}

.data-row:hover {
  background-color: #f8f9fa;
}

.loading-row,
.empty-row {
  text-align: center;
}

.loading-cell,
.empty-cell {
  padding: 40px;
  color: #666;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* 游戏表格内容样式 */
.game-name-cell {
  font-size: 15px;
  font-weight: 500;
  color: #333;
  line-height: 1.4;
  min-width: 150px;
}

.category-cell {
  min-width: 170px;
}

.category-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: flex-start;
}

.category-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: linear-gradient(135deg, #f0f7ff 0%, #e6f4ff 100%);
  border: 1px solid #b7dcff;
  color: #1677ff;
  font-size: 12px;
  line-height: 1.4;
  word-break: break-all;
}

.description-cell {
  min-width: 280px;
  max-width: 360px;
}

.description-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.description-text {
  line-height: 1.6;
  color: #333;
  display: -webkit-box;
  line-clamp: 4;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.description-text.expanded {
  display: block;
  line-clamp: unset;
  -webkit-line-clamp: unset;
  overflow: visible;
}

.description-toggle-btn {
  padding: 0;
  border: none;
  background: none;
  color: #1677ff;
  font-size: 13px;
  cursor: pointer;
}

.description-toggle-btn:hover {
  color: #4096ff;
  text-decoration: underline;
}

.game-cover {
  width: 120px;
  height: 68px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #eee;
  background-color: #f5f5f5;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.game-cover:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1;
}

.game-cover-placeholder {
  width: 120px;
  height: 68px;
  background-color: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}



.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  gap: 6px;
  transition: all 0.3s ease;
}

.status-badge.loading {
  opacity: 0.8;
}

.status-toggle-btn {
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

.status-toggle-btn:disabled {
  cursor: not-allowed;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.status-badge.active {
  background-color: #e6f7ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.status-badge.active .status-dot {
  background-color: #1890ff;
}

.status-badge.inactive {
  background-color: #fff1f0;
  color: #f5222d;
  border: 1px solid #ffa39e;
}

.status-badge.inactive .status-dot {
  background-color: #f5222d;
}

.status-badge.draft {
  background-color: #fffbe6;
  color: #faad14;
  border: 1px solid #ffe58f;
}

.status-badge.draft .status-dot {
  background-color: #faad14;
}

.action-btn {
  padding: 4px 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.actions-container {
  display: flex;
  gap: 8px;
  justify-content: flex-start;
  flex-wrap: nowrap;
}

.edit-btn {
  color: #1890ff;
  border-color: #1890ff;
}

.edit-btn:hover {
  background: #e6f7ff;
  border-color: #40a9ff;
}

.delete-btn {
  color: #ff4d4f;
  border-color: #ff4d4f;
}

.delete-btn:hover {
  background: #fff2f0;
  border-color: #ff7875;
}

/* 分页样式 */
.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 16px 0;
}

.pagination-info {
  color: #666;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  background-color: white;
  color: #333;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background-color: #f8f9fa;
  border-color: #007bff;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-number.active {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.page-numbers {
  display: flex;
  gap: 4px;
  margin: 0 8px;
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #eee;
}

/* 表单样式 */
.game-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-input,
.form-select,
.form-textarea {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.form-input.error,
.form-select.error {
  border-color: #dc3545;
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.error-message {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

/* 文件上传样式 */
.file-upload-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-input {
  display: none;
}

.upload-btn {
  padding: 8px 16px;
  background-color: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  transition: background-color 0.2s;
  align-self: flex-start;
}

.upload-btn:hover {
  background-color: #e9ecef;
}

.file-info,
.file-list {
  margin-top: 8px;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 4px;
}

.file-name {
  font-size: 14px;
  color: #333;
  flex: 1;
  margin-right: 8px;
  word-break: break-all;
}

.remove-btn {
  padding: 4px 8px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
  transition: background-color 0.2s;
}

.remove-btn:hover {
  background-color: #c82333;
}

/* 按钮样式 */
.cancel-btn,
.submit-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.cancel-btn {
  background-color: #6c757d;
  color: white;
}

.cancel-btn:hover {
  background-color: #545b62;
}

.submit-btn {
  background-color: #007bff;
  color: white;
}

.submit-btn:hover:not(:disabled) {
  background-color: #0056b3;
}

.submit-btn:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

/* 多选下拉框样式 */
.multi-select-container {
  position: relative;
  width: 100%;
}

.multi-select-display {
  padding: 10px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 20px;
  transition: border-color 0.3s ease;
}

.multi-select-display:hover {
  border-color: #40a9ff;
}

.multi-select-display:focus-within {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.multi-select-display.error {
  border-color: #ff4d4f;
}

.multi-select-display.error:focus-within {
  border-color: #ff4d4f;
  box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2);
}

.placeholder {
  color: #999;
}

.selected-items {
  color: #333;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-arrow {
  color: #999;
  font-size: 12px;
  transition: transform 0.3s ease;
  margin-left: 8px;
}

.dropdown-arrow.open {
  transform: rotate(180deg);
}

.multi-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 200px;
  overflow: hidden;
}

.dropdown-options {
  max-height: 150px;
  overflow-y: auto;
}

.dropdown-option {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s ease;
}

.dropdown-option:hover {
  background: #f5f5f5;
}

.dropdown-option.selected {
  background: #e6f7ff;
  color: #1890ff;
}

.dropdown-option input[type="checkbox"] {
  margin: 0;
  cursor: pointer;
}

/* 搜索区域多选下拉框样式 */
.search-multi-select {
  min-width: 120px;
  max-width: 150px;
}

.search-multi-select .multi-select-display {
  padding: 8px 12px;
  font-size: 14px;
  min-height: 36px;
}

.search-multi-select .placeholder {
  color: #999;
  font-size: 14px;
}

.search-multi-select .selected-items {
  font-size: 14px;
}

/* 复选框样式 */
.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: normal !important;
}

.checkbox-input {
  width: 18px;
  height: 18px;
  margin-right: 8px;
  cursor: pointer;
}

.checkbox-text {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.checkbox-hint {
  font-size: 12px;
  color: #999;
  margin-left: 8px;
}

/* 文件预览样式 */
.file-info-with-preview {
  display: flex;
  align-items: center;
  gap: 12px;
}

.file-item-with-preview {
  display: flex;
  align-items: center;
  gap: 12px;
}

.file-preview-image {
  width: 160px;
  height: 90px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.file-preview-image-small {
  width: 100px;
  height: 56px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;
}
</style>
